import { Component, Input, OnDestroy, OnInit, SimpleChanges, ViewChild } from "@angular/core";
import { Subject } from "rxjs";
import { TableVirtualScrollDataSource } from 'ng-table-virtual-scroll';
import { MatSort } from "@angular/material/sort";
import { tableColorScheme } from "src/app/enums";
import { takeUntil } from "rxjs/operators";
import { DialogDownloadTableComponent } from "src/app/common/dialogs/dialog-download-table/dialog-download-table.component";
import { MatDialog } from "@angular/material/dialog";
import { I18NextPipe } from "angular-i18next";
import { DashboardService } from "src/app/services/services/dashboard.service";
import * as _ from 'lodash';

@Component({
    selector: 'app-web-engage-table',
    templateUrl: './web-engage-table.component.html',
    styleUrls: ['./web-engage-table.component.scss'],
    standalone: false
})

export class WebEngageTableComponent implements OnInit, OnDestroy {
    @ViewChild(MatSort) sort: MatSort;
    @Input() public gridItem;
    @Input() public data;
    @Input() public columns: any[];
    @Input() public notifier: Subject<any>;
    @Input() public downloadDialogNotifier: Subject<any>;
    @Input() public isStandAlone: boolean;
    @Input() public reportCode: string;
    @Input() public reportInfo: string;
    @Input() public reportTitle: string;

    public formattedData: any[] = [];
    public dataSource: TableVirtualScrollDataSource<any> = new TableVirtualScrollDataSource([]);
    public displayedColumns: string[] = [];
    public reducedGroups = [];
    public hasGrouping: boolean = false;
    public styles = {
        header: "",
        oddRow: "",
        evenRow: "",
        headerCol: "",
        rowCol: "",
        isDarkTheme: false
    }

    private ngUnsubscribe = new Subject<void>();
    groupNames: string[] = [];

    headerFontSize: number = 14;
    rowFontSize: number = 14;
    headerColor: string = "";
    rowColor: string = "";

    constructor(
        private i18nextPipe: I18NextPipe,
        public dialog: MatDialog,
        private DashboardsService: DashboardService) { }

    ngOnInit(): void {
        this.notifier.pipe(takeUntil(this.ngUnsubscribe)).subscribe(ev => {
            setTimeout(() => {
                if (this.data?.length > 0) {
                    this.hasGrouping = this.gridItem.setup.sysGroupByTime.trim().length > 0;
                    if (this.hasGrouping) {
                        this.setDisplayedColumnsWithGrouping();
                        this.formatDataWithGrouping();
                    } else {
                        this.setDisplayedColumns();
                        this.formatDataWithoutGrouping();
                    }
                    this.setTableStyles();
                    this.buildDataSource();
                    this.setFontSizes();
                    this.setColors();
                }
            }, 0)
        });

        if (this.isStandAlone) {
            this.downloadDialogNotifier.pipe(takeUntil(this.ngUnsubscribe)).subscribe(value => {
                this.exportTable();
            });
        }
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }

    ngOnChanges(changes: SimpleChanges) {
        // only when column order is changed
        if (changes["columns"] && changes["columns"].previousValue) {
            if (this.hasGrouping) {
                this.setDisplayedColumnsWithGrouping();
            } else {
                this.setDisplayedColumns();
            }
        }
    }

    getColumnName(columnId: string) {
        return this.columns.find(c => c.id === columnId)?.name ?? "";
    }

    getColumnFormat(columnId: string) {
        return this.columns.find(c => c.id === columnId)?.format ?? "";
    }

    getColumnType(columnId: string) {
        return this.columns.find(c => c.id === columnId)?.dbType ?? "";
    }

    exportTable() {
        let exportColumns = this.setExportColumns();

        const dialogRef = this.dialog.open(DialogDownloadTableComponent, {
            panelClass: 'dialog-standard-pannel',
            backdropClass: "backdrop-invisible",
            data: {
                fileName: this.reportCode,
                reportCode: this.reportCode,
                fileType: "XLSX",
                fileTypes: ["XLSX", "CSV (Comma Delimmited)"],
                isChart: false
            },
            width: "495px",
            height: "305px"
        });

        dialogRef.afterClosed().subscribe(data => {
            if (data) {
                let fileName = data.fileName;
                let fileType = data.fileType;

                let exportData = this.setExportData();

                let model = {
                    data: [...exportData],
                    exportColumns: exportColumns,
                    reportInfo: this.reportInfo,
                    reportTitle: this.reportTitle
                };

                if (fileType.toLowerCase() === "xlsx") {
                    this.DashboardsService.exportTableXLSX(model).subscribe(blob => {
                        const blobUrl = URL.createObjectURL(blob);
                        const link = document.createElement("a");

                        link.href = blobUrl;
                        link.download = `${fileName}.xlsx`;

                        document.body.appendChild(link);

                        link.dispatchEvent(
                            new MouseEvent('click', {
                                bubbles: true,
                                cancelable: true,
                                view: window
                            })
                        );

                        document.body.removeChild(link);
                    });
                } else {
                    this.DashboardsService.exportTableCSV(model).subscribe(blob => {
                        const blobUrl = URL.createObjectURL(blob);
                        const link = document.createElement("a");

                        link.href = blobUrl;
                        link.download = `${fileName}.csv`;

                        document.body.appendChild(link);

                        link.dispatchEvent(
                            new MouseEvent('click', {
                                bubbles: true,
                                cancelable: true,
                                view: window
                            })
                        );

                        document.body.removeChild(link);
                    })
                }
            }
        });
    }

    setExportColumns() {
        let exportColumns = [];
        if (this.hasGrouping) {
            exportColumns = [...["Rule name", "Interaction name"], ...this.displayedColumns];
        } else {
            exportColumns = this.displayedColumns;
        }

        return exportColumns;
    }

    setExportData() {
        let exportData = [];
        if (this.hasGrouping) {
            let groupedData = [];
            for (let i = 0; i < this.groupNames.length; i++) {
                let groupNameArr = this.formattedData.filter(d => d["_groupName"] === this.groupNames[i]);

                groupedData = [...groupedData, ...groupNameArr];
            }
            for (let i = 0; i < groupedData.length; i++) {
                let exportObj = groupedData[i];
                exportObj["Rule name"] = groupedData[i]["_originalEntry"]["rule_name"];
                exportObj["Interaction name"] = groupedData[i]["_originalEntry"]["interaction_name"];
                exportData.push(exportObj);
            }
        } else {
            for (let i = 0; i < this.formattedData.length; i++) {
                let exportObj = _.cloneDeep(this.formattedData[i]);
                exportObj["Rule name"] = exportObj["_originalEntry"]["rule_name"];
                exportData.push(exportObj);
            }
        }

        return exportData;
    }

    setDisplayedColumns() {
        if (this.data?.length > 0) {
            let displayedColumns = [];

            for (let i = 0; i < this.columns.length; i++) {
                displayedColumns.push(this.columns[i].name);
            }

            this.displayedColumns = displayedColumns;
        }
    }

    setDisplayedColumnsWithGrouping() {
        if (this.data?.length > 0) {
            let displayedColumns = ["Time"];

            let columns = this.columns.filter(c => c.id !== "rule_name" && c.id !== "interaction_name");

            for (let i = 0; i < columns.length; i++) {
                displayedColumns.push(columns[i].name);
            }

            this.displayedColumns = displayedColumns;
        }
    }

    sortData(e): void {
        if (this.hasGrouping) {
            this.formattedData.sort((a, b) => {
                if (e.active === "Time") {
                    if (e.direction === "asc") {
                        return new Date(a["_originalEntry"]["from_date"]).getTime() - new Date(b["_originalEntry"]["from_date"]).getTime();
                    } else {
                        return new Date(b["_originalEntry"]["from_date"]).getTime() - new Date(a["_originalEntry"]["from_date"]).getTime();
                    }
                }

                if (e.direction === "asc") {
                    { return a[e.active] - b[e.active] };
                }

                if (e.direction === "desc") {
                    { return b[e.active] - a[e.active] };
                }
            });
        } else {
            let sortedData = [];
            let preSortedData = [];
            let leadingRuleEntries = this.formattedData.filter(d => d["Rule name"].trim().length > 0);
            for (let i = 0; i < leadingRuleEntries.length; i++) {
                let ruleEntry = leadingRuleEntries[i];
                let ogRuleEntries = this.formattedData.filter(
                    (d) => d["_originalEntry"]["rule_name"] === ruleEntry["Rule name"]).map(
                        (entry) => {
                            return {
                                ...entry,
                                "Rule name": ""
                            }
                        });
                if (e.direction === "asc") {
                    ogRuleEntries.sort((a, b) => { return a[e.active] - b[e.active] });
                }

                if (e.direction === "desc") {
                    ogRuleEntries.sort((a, b) => { return b[e.active] - a[e.active] });
                }

                ogRuleEntries[0]["Rule name"] = ruleEntry["Rule name"];
                preSortedData.push(ogRuleEntries);
            }

            for (let i = 0; i < preSortedData.length; i++) {
                preSortedData[i].sort((a, b) => {
                    if (e.direction === "asc") {
                        { return a[e.active] - b[e.active] };
                    }

                    if (e.direction === "desc") {
                        { return b[e.active] - a[e.active] };
                    }
                });
            }

            preSortedData.sort((a, b) => {
                if (e.direction === "asc") {
                    { return a[0][e.active] - b[0][e.active] };
                }

                if (e.direction === "desc") {
                    { return b[0][e.active] - a[0][e.active] };
                }
            })

            for (let i = 0; i < preSortedData.length; i++) {
                sortedData = [...sortedData, ...preSortedData[i]]
            }

            this.formattedData = this.setOddRows([...sortedData]);
        }

        this.buildDataSource();
    }

    formatDataWithoutGrouping() {
        let dataByRule = [];
        let formattedData = [];

        for (let i = 0; i < this.data.length; i++) {
            let ogEntry = this.data[i];
            let newEntry = {
                "_originalEntry": ogEntry
            };
            let keys = Object.keys(ogEntry);
            for (let j = 0; j < keys.length; j++) {
                let name = this.getColumnName(keys[j]);
                if (name !== "") {
                    newEntry[name] = ogEntry[keys[j]];
                }
            }
            formattedData.push(newEntry);
        }

        let rules = [... new Set(formattedData.map((x) => x["Rule name"]))];

        for (let i = 0; i < rules.length; i++) {
            let currentRulesRowData = formattedData.filter(x => x["Rule name"] === rules[i]);
            for (let j = 0; j < currentRulesRowData.length; j++) {
                if (j !== 0) currentRulesRowData[j]["Rule name"] = "";
            }

            dataByRule = [...dataByRule, ...currentRulesRowData];
        }

        this.formattedData = this.setOddRows(dataByRule);
    }

    formatDataWithGrouping() {
        let formattedData = [];
        for (let i = 0; i < this.data.length; i++) {
            let ogEntry = this.data[i];
            let groupName = `${ogEntry["rule_name"]} - ${ogEntry["interaction_name"]}`;
            if (this.groupNames.indexOf(groupName) === -1) {
                this.groupNames.push(groupName);
            }
            let newEntry = {
                "Time": new Date(ogEntry["from_date"]).toLocaleString('en-UK'),
                "_groupName": groupName,
                "_originalEntry": ogEntry
            };
            let keys = Object.keys(ogEntry);
            for (let j = 0; j < keys.length; j++) {
                let name = this.getColumnName(keys[j]);
                if (name !== "") {
                    newEntry[name] = ogEntry[keys[j]];
                }
            }
            formattedData.push(newEntry);
        }

        this.formattedData = this.setOddRows(formattedData);
    }

    buildDataSource() {
        if (this.gridItem.setup.sysGroupByTime.trim().length > 0) {
            this.dataSource = new TableVirtualScrollDataSource(this.groupBy("_groupName", this.formattedData, this.reducedGroups));
        } else {
            this.dataSource = new TableVirtualScrollDataSource(this.formattedData);
        }
    }

    groupBy(column: string, data: any[], reducedGroups?: any[]) {
        if (!column || column === " ") return [...data];
        let collapsedGroups = reducedGroups;
        if (!reducedGroups) collapsedGroups = [];
        const customReducer = (accumulator, currentValue) => {
            let currentGroup = currentValue[column];
            if (!accumulator[currentGroup])
                accumulator[currentGroup] = [{
                    groupName: `${currentValue[column]}`,
                    value: currentValue[column],
                    isGroup: true,
                    reduced: collapsedGroups.some((group) => group.value == currentValue[column])
                }];

            accumulator[currentGroup].push(currentValue);

            return accumulator;
        }
        let groups = data.reduce(customReducer, {});
        let keys = Object.keys(groups);
        let groupArray = keys.map(key => groups[key]);

        let flatList = groupArray.reduce((a, c) => { return a.concat(c); }, []);
        return flatList.filter((rawLine) => {
            return rawLine.isGroup ||
                collapsedGroups.every((group) => rawLine[column] != group.value);
        });
    }

    reduceGroup(row) {
        row.reduced = !row.reduced;
        if (row.reduced)
            this.reducedGroups.push(row);
        else
            this.reducedGroups = this.reducedGroups.filter((el) => el.value != row.value);

        this.buildDataSource();
    }

    isGroup(index, item): boolean {
        return item.isGroup;
    }

    setOddRows(formattedData): any {
        if (this.hasGrouping) {
            let groupingValue = formattedData[0]["_groupName"];
            let index = 1;
            for (let i = 0; i < formattedData.length; i++) {
                let currentGroupingValue = formattedData[i]["_groupName"];
                if (groupingValue === currentGroupingValue) {
                    formattedData[i]["isOdd"] = index % 2 === 0 ? false : true;
                    index++;
                } else {
                    index = 1;
                    groupingValue = currentGroupingValue;
                    formattedData[i]["isOdd"] = index % 2 === 0 ? false : true;
                    index++;
                }
            }
        } else {
            for (let i = 0; i < formattedData.length; i++) {
                formattedData[i]["isOdd"] = i % 2 === 0 ? true : false;
            }
        }

        return formattedData;
    }

    setTableStyles(): void {
        const theme = this.gridItem.colorScheme ? tableColorScheme[this.gridItem.colorScheme].toLowerCase() : "default";
        this.styles = {
            header: theme + "Header",
            oddRow: theme + "OddRow",
            evenRow: theme + "EvenRow",
            headerCol: theme + "HeaderCol",
            rowCol: theme + "RowCol",
            isDarkTheme: theme !== "default" && theme !== "ash"
        };
    }

    setFontSizes(): void {
        const preferences = this.gridItem.setup.preferences;
        if (preferences) {
            const fonts = preferences.fonts;
            if (fonts?.length > 0) {
                if (fonts.some(f => f.id === 6)) {
                    let header = fonts.filter(f => f.id === 6)[0];
                    this.headerFontSize = header.fontSize;
                }

                if (fonts.some(f => f.id === 7)) {
                    let row = fonts.filter(f => f.id === 7)[0];
                    this.rowFontSize = row.fontSize;
                }
            }
        }
    }

    setColors(): void {
        const preferences = this.gridItem.setup.preferences;
        if (preferences) {
            const colors = preferences.colors;
            if (colors?.length > 0) {
                if (colors.some(f => f.id === 6)) {
                    let header = colors.filter(f => f.id === 6)[0];
                    this.headerColor = header.color;
                }

                if (colors.some(f => f.id === 7)) {
                    let header = colors.filter(f => f.id === 7)[0];
                    this.rowColor = header.color;
                }
            }
        }
    }
}
